import { useCallback, useMemo, useEffect } from 'react';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';
import queryString from 'query-string';
import { navigate } from '@reach/router';
import * as R from 'ramda';
import { debounce } from './ramdaExtension';
import { GET_ADS } from '../apollo/graphql/ads';
import { GET_USER_INFO, GET_HKDU_USER_INFO } from '../apollo/graphql/user';

const QUERY_PROPERTY = {
  PAGE: 'page',
  TAG: 'tag',
  CATEGORY: 'category',
  SEARCH: 'search',
};

export const usePagesBindingUrl = (query, uri) => {
  const page = Number(query.page || 1) || 1;
  const onPageChange = useCallback(
    page => {
      navigate(`${uri}/?${queryString.stringify({ ...query, [QUERY_PROPERTY.PAGE]: page })}`);
    },
    [uri, query]
  );

  return [page, onPageChange];
};

export const useFilterTags = (query, uri) => {
  const tag = R.propOr('All', 'tag', query);
  const handleTagChange = useCallback(
    tag => {
      navigate(
        `${uri}/?${queryString.stringify({ ...query, [QUERY_PROPERTY.PAGE]: 1, [QUERY_PROPERTY.TAG]: tag })}`
      );
    },
    [query, uri]
  );

  return [tag, handleTagChange];
};

export const useFilterCategorys = (query, uri) => {
  const category = R.propOr('All', 'category', query);
  const handleCategoryChange = useCallback(
    category => {
      navigate(
        `${uri}/?${queryString.stringify({
          ...query,
          [QUERY_PROPERTY.PAGE]: 1,
          [QUERY_PROPERTY.CATEGORY]: category,
        })}`
      );
    },
    [query, uri]
  );

  return [category, handleCategoryChange];
};

export const useSearchText = (query, uri) => {
  const searchText = R.propOr('', 'search', query);
  const search = useMemo(
    () =>
      debounce(200, text =>
        navigate(
          `${uri}/?${queryString.stringify({
            ...query,
            [QUERY_PROPERTY.SEARCH]: text,
            [QUERY_PROPERTY.PAGE]: 1,
          })}`
        )
      ),
    [query, uri]
  );
  const handleSearch = useCallback(event => search(event.target.value), [search]);

  return [searchText, handleSearch];
};

export const useAdsQuery = (uri = '') => {
  const { data, loading, error } = useQuery(GET_ADS, {
    variables: { name: `${uri}/` },
  });

  return { ads: R.pathOr([], ['ads', 0, 'banners'], data), loading, error };
};

export const useUser = () => {
  const userInfoQuery = useQuery(GET_USER_INFO);
  const [getHKDUUserInfo, HKDUUserInfoQuery] = useLazyQuery(GET_HKDU_USER_INFO);
  const useId = R.pathOr(null, ['data', 'user', 'id'], userInfoQuery);

  useEffect(() => {
    if (useId && !HKDUUserInfoQuery.called) {
      getHKDUUserInfo({
        variables: {
          id: useId,
        },
      });
    }
  }, [HKDUUserInfoQuery.called, getHKDUUserInfo, useId]);

  return {
    userInfo: R.pathOr(null, ['data', 'user'], userInfoQuery),
    HKDUUserInfo: R.pathOr(null, ['data', 'user', '0'], HKDUUserInfoQuery),
  };
};
